import type { Hub } from '../types/hub';
import type { Program } from '../types/program';
import { hubPlaceHref } from './hubChapter';
import { placePath } from './placePaths';
import { hubHasDocuments } from './hubSearch';

export interface LibraryPlace {
  kind: 'program' | 'hub';
  id: string;
  title: string;
  href: string;
  slug: string;
}

export interface RowPlace {
  kind: 'hub' | 'program';
  title: string;
  href: string;
  slug: string;
}

/** WordPress posts linked from a program's hubs or highlighted on the program itself. */
export function programDocumentIds(program: Program): number[] {
  const ids = new Set<number>();
  for (const highlight of program.highlights) ids.add(highlight.wpPostId);
  for (const hub of program.hubs) {
    for (const resource of hub.resources) ids.add(resource.wpPostId);
  }
  return [...ids];
}

export function libraryDoorPrograms(programs: Program[]): Program[] {
  return programs.filter((program) => programDocumentIds(program).length > 0);
}

/** Hubs with documents and no published program. Programmed hubs are steps inside the program door. */
export function libraryDoorHubs(hubs: Hub[]): Hub[] {
  return hubs
    .filter((hub) => hubHasDocuments(hub) && !hub.program)
    .sort((a, b) => a.title.localeCompare(b.title));
}

function titleScore(title: string, query: string): number | null {
  const q = query.trim().toLowerCase();
  if (!q) return null;
  const value = title.trim().toLowerCase();
  if (value === q) return 0;
  if (value.startsWith(q)) return 1;
  if (value.includes(q)) return 2;
  return null;
}

/** Title matches only. Closest title first. Includes hubs that live inside a program. */
export function matchingPlaces(query: string, programs: Program[], hubs: Hub[]): LibraryPlace[] {
  const ranked: { score: number; length: number; place: LibraryPlace }[] = [];
  const add = (title: string, place: LibraryPlace) => {
    const score = titleScore(title, query);
    if (score == null) return;
    ranked.push({ score, length: title.length, place });
  };
  for (const program of programs) {
    add(program.title, {
      kind: 'program',
      id: program.id,
      title: program.title,
      href: placePath(program),
      slug: program.slug,
    });
  }
  for (const hub of hubs) {
    if (!hubHasDocuments(hub)) continue;
    add(hub.title, {
      kind: 'hub',
      id: hub.id,
      title: hub.title,
      href: hubPlaceHref(hub),
      slug: hub.slug,
    });
  }
  return ranked
    .sort((a, b) => a.score - b.score || a.length - b.length || a.place.title.localeCompare(b.place.title))
    .map((item) => item.place);
}

export function rowPlaceIndex(hubs: Hub[], programs: Program[]): Map<number, RowPlace[]> {
  const map = new Map<number, RowPlace[]>();
  const add = (wpId: number, place: RowPlace) => {
    const list = map.get(wpId) ?? [];
    if (!list.some((item) => item.href === place.href)) list.push(place);
    map.set(wpId, list);
  };
  for (const hub of hubs) {
    if (!hubHasDocuments(hub)) continue;
    const place: RowPlace = {
      kind: 'hub',
      title: hub.title,
      href: hubPlaceHref(hub),
      slug: hub.slug,
    };
    for (const resource of hub.resources) add(resource.wpPostId, place);
  }
  for (const program of programs) {
    const place: RowPlace = {
      kind: 'program',
      title: program.title,
      href: placePath(program),
      slug: program.slug,
    };
    for (const highlight of program.highlights) {
      if (map.get(highlight.wpPostId)?.some((item) => item.kind === 'hub')) continue;
      add(highlight.wpPostId, place);
    }
  }
  return map;
}

export function documentMembership(
  wpId: number,
  hubs: Hub[],
  programs: Program[]
): { hubs: RowPlace[]; programs: RowPlace[] } {
  const hubPlaces: RowPlace[] = [];
  const programPlaces: RowPlace[] = [];
  const seenPrograms = new Set<string>();
  const addProgram = (program: { id: string; title: string; slug: string }) => {
    if (seenPrograms.has(program.id)) return;
    seenPrograms.add(program.id);
    programPlaces.push({
      kind: 'program',
      title: program.title,
      href: placePath(program),
      slug: program.slug,
    });
  };

  for (const hub of hubs) {
    if (!hub.resources.some((resource) => resource.wpPostId === wpId)) continue;
    hubPlaces.push({
      kind: 'hub',
      title: hub.title,
      href: hubPlaceHref(hub),
      slug: hub.slug,
    });
    if (hub.program) addProgram(hub.program);
  }
  for (const program of programs) {
    const highlighted = program.highlights.some((highlight) => highlight.wpPostId === wpId);
    const inHub = program.hubs.some((hub) => hub.resources.some((resource) => resource.wpPostId === wpId));
    if (highlighted || inHub) addProgram(program);
  }

  hubPlaces.sort((a, b) => a.title.localeCompare(b.title));
  programPlaces.sort((a, b) => a.title.localeCompare(b.title));
  return { hubs: hubPlaces, programs: programPlaces };
}

/** Other documents in hubs that contain this post. The hub the reader came from comes first. */
export function sameHubPostIds(wpId: number, hubs: Hub[], preferredHubSlug: string | null, limit: number): number[] {
  const containing = hubs
    .filter((hub) => hub.resources.some((resource) => resource.wpPostId === wpId))
    .sort((a, b) => {
      if (a.slug === preferredHubSlug) return -1;
      if (b.slug === preferredHubSlug) return 1;
      return a.title.localeCompare(b.title);
    });
  const ids: number[] = [];
  for (const hub of containing) {
    const siblings = [...hub.resources].sort((a, b) => a.order - b.order);
    for (const resource of siblings) {
      if (resource.wpPostId === wpId || ids.includes(resource.wpPostId)) continue;
      ids.push(resource.wpPostId);
      if (ids.length >= limit) return ids;
    }
  }
  return ids;
}
